import * as React from 'react';
import { Button } from '@teamsnap/teamsnap-ui';
import { FieldType } from '../types';

import './AdditionalQuestionConditionalLogic.scss';

interface DropdownOption {
  text: string;
  value: string;
}

export interface VisibilityRule {
  id: string;
  comparison: ComparisonType;
  question: string;
  condition: ConditionType;
  answer: string;
}

export enum ConditionType {
  EQUAL = 'eq',
  NOT_EQUAL = 'neq',
  GREATER_THAN = 'gt',
  GREATER_THAN_EQUAL = 'gte',
  LESS_THAN = 'lt',
  LESS_THAN_EQUAL = 'lte',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
}

const CONDITION_DISPLAY = {
  [ConditionType.EQUAL]: 'Is',
  [ConditionType.NOT_EQUAL]: 'Is Not',
  [ConditionType.GREATER_THAN]: 'Greater Than',
  [ConditionType.GREATER_THAN_EQUAL]: 'Greater Than or Equal To',
  [ConditionType.LESS_THAN]: 'Less Than',
  [ConditionType.LESS_THAN_EQUAL]: 'Less Than or Equal To',
  [ConditionType.CONTAINS]: 'Contains',
  [ConditionType.NOT_CONTAINS]: 'Does not contain',
};

const MULTI_CONDITION_DISPLAY = {
  [ConditionType.EQUAL]: 'Is',
  [ConditionType.NOT_EQUAL]: 'Is Not',
  [ConditionType.CONTAINS]: 'Includes',
  [ConditionType.NOT_CONTAINS]: 'Does not include',
};

export enum ComparisonType {
  AND = 'and',
  OR = 'or',
}

type Props = {
  question: FieldType;
  field: VisibilityRule;
  index: number;
  options: FieldType[];
  fields: VisibilityRule[];
  formHook: any;
  comparison: ComparisonType;
  onRemove: (number) => void;
  setComparison: (boolean) => void;
};

const getSection = (field: FieldType) => {
  if (field.target === 'participant' && !field.organization_id) {
    return 'Participant Information';
  } else if (field.target === 'guardian' && !field.organization_id) {
    return 'Guardian Information';
  } else {
    return 'Additional Question';
  }
};

const mapQuestionOptions = (question: FieldType): DropdownOption => {
  const isParticipationGroup =
    question.internal && question.name === 'participation_group';

  return {
    text: isParticipationGroup
      ? 'Participation Group'
      : `${question.question_number}. ${question.label} - ${getSection(
          question
        )}`,
    value: `${question.id}`,
  };
};

const buildQuestionOptions = (
  question: FieldType,
  options: FieldType[]
): DropdownOption[] =>
  options.filter((q) => q.id !== question.id).map(mapQuestionOptions);

const hasError = (index, name, errors) =>
  errors?.logic &&
  errors?.logic[`${index}`] &&
  errors.logic[`${index}`]?.[`${name}`];

const ConditionalLogic = ({
  question,
  field,
  index,
  options,
  fields,
  formHook,
  comparison,
  onRemove,
  setComparison,
}: Props) => {
  const [answerOptions, setAnswerOptions] = React.useState([]);
  const [questionOptions, setQuestionOptions] = React.useState([]);
  const [conditionOptions, setConditionOptions] = React.useState([]);
  const { register, watch, setValue, errors } = formHook;

  const selectedQuestion = watch(`logic[${index}].question`);

  React.useEffect(() => {
    let condOptions = [
      ConditionType.EQUAL,
      ConditionType.NOT_EQUAL,
      ConditionType.CONTAINS,
      ConditionType.NOT_CONTAINS,
    ];
    let multiSelect = false;
    if (selectedQuestion) {
      const question = options.find((o) => o.id === +selectedQuestion);
      multiSelect = question.multiselect;

      if (question.multiselect) {
        condOptions = [ConditionType.CONTAINS, ConditionType.NOT_CONTAINS];
      } else {
        condOptions = [ConditionType.EQUAL, ConditionType.NOT_EQUAL];
      }
      setAnswerOptions(
        question.options.map((i) => ({
          text: i,
          value: i,
        }))
      );
    }

    setConditionOptions(
      condOptions.map((o) => ({
        value: o,
        text: (multiSelect ? MULTI_CONDITION_DISPLAY : CONDITION_DISPLAY)[o],
      }))
    );
  }, [selectedQuestion]);

  React.useEffect(() => {
    const questions = buildQuestionOptions(question, options);
    setQuestionOptions(questions);
  }, [fields]);

  React.useEffect(() => {
    const id = fields[index].id;
    const question = fields[index].question;
    const condition = fields[index].condition;

    if (id !== '') {
      setValue(`logic[${index}].id`, id);
    }
    if (question !== '') {
      setValue(`logic[${index}].question`, question);
    }

    if (condition) {
      setValue(`logic[${index}].condition`, condition);
    }
  }, [questionOptions]);

  React.useEffect(() => {
    const answer = fields[index].answer;
    if (answer !== '') {
      setValue(`logic[${index}].answer`, answer);
    }
  }, [answerOptions]);

  React.useEffect(() => {
    setValue(`logic[${index}].comparison`, comparison);
  }, [comparison]);

  return (
    <>
      <input
        type="hidden"
        name={`logic[${index}].id`}
        defaultValue={field.id}
        ref={register()}
      />
      <div className={`u-flex u-spaceTopSm ${index === 0 ? 'u-hidden' : ''}`}>
        <div className="u-size1of6 u-padRightSm u-spaceBottomSm">
          <div className="SelectBox">
            <select
              className="SelectBox-options"
              name={`logic[${index}].comparison`}
              data-testid={`logic[${index}].comparison`}
              ref={register({ required: 'You must select an option.' })}
              defaultValue={field.comparison}
              onChange={(e) => {
                setComparison(e.target.value);
              }}
            >
              <option value={ComparisonType.AND}>
                {ComparisonType.AND.toString().toUpperCase()}
              </option>
              <option value={ComparisonType.OR}>
                {ComparisonType.OR.toString().toUpperCase()}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div>
        <div className="SelectBox">
          <select
            className={`SelectBox-options ${
              hasError(index, 'question', errors) ? 'u-borderColorNegative' : ''
            }`}
            name={`logic[${index}].question`}
            data-testid={`logic[${index}].question`}
            ref={register({ required: 'Select a question.' })}
            defaultValue={field.question}
          >
            <option value="">Select question</option>
            {questionOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.text}
              </option>
            ))}
          </select>
        </div>
        {hasError(index, 'question', errors) && (
          <p className="u-colorNegative u-padSidesXs u-padTopXs">
            {errors['logic'][`${index}`].question.message}
          </p>
        )}
      </div>
      <div className="u-flex u-spaceEndsSm">
        <div className="u-size1of4 u-padRightSm">
          <div>
            <div className="SelectBox">
              <select
                className={`SelectBox-options ${
                  hasError(index, 'condition', errors)
                    ? 'u-borderColorNegative'
                    : ''
                }`}
                name={`logic[${index}].condition`}
                data-testid={`logic[${index}].condition`}
                ref={register({ required: 'Select a condition.' })}
                defaultValue={field.condition}
              >
                <option value="">Select condition</option>
                {conditionOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.text}
                  </option>
                ))}
              </select>
            </div>
            {hasError(index, 'condition', errors) && (
              <p className="u-colorNegative u-padSidesXs u-padTopXs">
                {errors['logic'][`${index}`].condition.message}
              </p>
            )}
          </div>
        </div>
        <div className="u-size3of4">
          <div>
            <div className="SelectBox">
              <select
                className={`SelectBox-options ${
                  hasError(index, 'answer', errors)
                    ? 'u-borderColorNegative'
                    : ''
                }`}
                name={`logic[${index}].answer`}
                data-testid={`logic[${index}].answer`}
                ref={register({ required: 'Select an answer' })}
                defaultValue={field.answer}
                disabled={answerOptions.length === 0}
              >
                <option value="">Select answer</option>
                {answerOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.text}
                  </option>
                ))}
              </select>
            </div>
            {hasError(index, 'answer', errors) && (
              <p className="u-colorNegative u-padSidesXs u-padTopXs">
                {errors['logic'][`${index}`].answer.message}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="u-flex u-flexJustifyEnd u-padSidesMd">
        {fields.length > 1 && (
          <Button
            testId="delete-condition-button"
            type="link"
            icon="trash"
            mods="btn-delete-condition"
            onClick={() => onRemove(index)}
          >
            Delete
          </Button>
        )}
      </div>
    </>
  );
};

export default ConditionalLogic;
